import { PocListItem, PocSelectDropdown, PocTypography } from 'commons/components/components-wrapper';
import { InputValidity } from '@one/web-components/dist/types/components';
import { useVirtualization } from 'commons/hooks';
import { withOptionalLabel } from 'commons/utils/optional-label.utils';
import { useTranslation } from 'core/i18n/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssistiveText } from '../assistive-text';
import { getInlineOrBlockClassName } from '../utils/utils';
import { VirtualPadding } from './components';
import { computeRowHeight, getInitialExpandedItems } from './select.business';
import { SelectOption, SelectOptionItem } from './select-option-item.component';
import { SELECTED_OPTION_PLACEHOLDER_CLASS } from './select.constants';
import { OwcListitemHiddenStyled, OwcListStyled, SelectErrorsLayout, SelectLayout } from './select.style';
import { SelectProps, SelectPropsValue } from './select.type';
import { toggleSetItem } from './select.utils';
import { useSelectOptionsProjection } from './use-select-options-projection.hook';

export const Select: React.FC<SelectProps> = (props) => {
    const {
        id,
        name = '',
        label = undefined,
        placeholder,
        multiple = false,
        value = undefined,
        required = false,
        optional = !required,
        searchable = false,
        disabled = false,
        clearButton = false,
        options,
        validity,
        spaceForError = false,
        floatingAssistiveText = false,
        inline = false,
        size = 'small',
        prefixProps,
        className,
        extraAction,
        variant = 'subtitle1',
        customKeyItemPrefix = '',
        showSelectorControl = false,
        noBorder = false,
        validationMessages = {},
        customValidationCode,
        virtualized = false,
        hideSelectAll = false,
        autoComplete = 'off',
        onClickExtraAction,
        onChange,
        onValidity,
        onClear,
        onClose,
    } = props;
    const { t } = useTranslation();

    const scrollContainerRef = useRef<HTMLOwcListElement | null>(null);

    const initialExpandedItems = useMemo(() => getInitialExpandedItems(options), [options]);

    const [expandedItems, setExpandedItems] = useState<Set<string>>(initialExpandedItems);

    const [internalMultipleValue, setInternalMultipleValue] = useState<string[]>(
        multiple ? ((value as string[]) ?? []) : []
    );

    useEffect(() => {
        if (multiple) {
            setInternalMultipleValue((value as string[]) ?? []);
        }
    }, [multiple, value]);

    useEffect(() => {
        setExpandedItems(initialExpandedItems);
    }, [initialExpandedItems]);

    const getRowHeight = useCallback(
        (index: number) => computeRowHeight(options, expandedItems, index),
        [options, expandedItems]
    );

    const { shouldVirtualize, virtualPaddingTop, virtualPaddingBottom, measureElementRef, virtualRows } =
        useVirtualization<SelectOption>({
            disabled: !virtualized,
            overscan: 5,
            scrollContainerRef,
            estimateRowHeight: getRowHeight,
            data: options,
            measureDeps: [expandedItems],
        });

    const { itemsToRender, selectedOptionsLabel, isSelectedOptionPlaceholder, hiddenOptions } =
        useSelectOptionsProjection(options, value, virtualRows, shouldVirtualize);

    const handleToggleExpanded = useCallback((optionKey: string, isExpanded: boolean) => {
        setExpandedItems((prev) => toggleSetItem(prev, optionKey, isExpanded));
    }, []);

    const handleDropdownClick = useCallback((ev: React.MouseEvent) => {
        ev.stopPropagation();
    }, []);

    const handleValueChange = useCallback(
        (ev: CustomEvent<SelectPropsValue>) => {
            if (multiple) {
                const next = (ev.detail as string[]) ?? [];
                setInternalMultipleValue(next);
                onChange(name, next);
            } else {
                onChange(name, ev.detail);
            }
        },
        [multiple, name, onChange]
    );

    const handleValidityChange = useCallback(
        (ev: CustomEvent<InputValidity>) => {
            onValidity?.(name, ev.detail);
        },
        [name, onValidity]
    );

    const handleVisibleChange = useCallback(
        (ev: CustomEvent<boolean>) => {
            if (!ev.detail) {
                setExpandedItems(initialExpandedItems);
                onClose?.();
            }
        },
        [initialExpandedItems, onClose]
    );

    // owc-select-dropdown caches the selected option's text and only re-reads it on a new menuProps.
    const menuProps = useMemo(
        () => ({ listProps: { size, showSelectorControl, noBorder } }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedOptionsLabel forces that re-read on rename
        [size, showSelectorControl, noBorder, selectedOptionsLabel]
    );

    const clearButtonProps = useMemo(() => ({ onclick: onClear }), [onClear]);

    const renderedOptions = useMemo(
        () =>
            itemsToRender.map(({ index, option }) => {
                const optionKey = String(option.value);
                return (
                    <div
                        key={`op_${customKeyItemPrefix}_${option.value}`}
                        data-index={index}
                        ref={shouldVirtualize ? measureElementRef : undefined}
                    >
                        <SelectOptionItem
                            option={option}
                            name={name}
                            optionKey={optionKey}
                            expanded={expandedItems.has(optionKey)}
                            onToggleExpanded={handleToggleExpanded}
                        />
                    </div>
                );
            }),
        [
            itemsToRender,
            customKeyItemPrefix,
            name,
            expandedItems,
            handleToggleExpanded,
            shouldVirtualize,
            measureElementRef,
        ]
    );

    return (
        <SelectLayout
            data-testid={`select-layout-${name}`}
            className={`${getInlineOrBlockClassName(inline)} ${className} ${isSelectedOptionPlaceholder ? SELECTED_OPTION_PLACEHOLDER_CLASS : ''}`}
        >
            {label && (
                <PocTypography
                    element="span"
                    variant="subtitle1"
                    className={`${variant === 'subtitle1' ? 'label' : ''} ${getInlineOrBlockClassName(inline)}`}
                >
                    {withOptionalLabel(t, t(label), optional)}
                </PocTypography>
            )}
            {/* workaround */}
            <div className={`dropdown-container`} style={{ width: inline ? '40%' : '100%' }}>
                <PocSelectDropdown
                    {...(id ? { id } : { name })}
                    className={` ${inline ? 'inline' : 'block'}`}
                    name={name}
                    label={size === 'small' ? name : undefined}
                    data-testid={`select-form-${name}`}
                    placeholder={t(placeholder!) ?? undefined}
                    variant="outlined"
                    size={size}
                    no-clear-icon={!clearButton}
                    menuProps={menuProps}
                    value={multiple ? internalMultipleValue : value}
                    multiple={multiple}
                    required={required}
                    searchable={searchable}
                    disabled={disabled}
                    validity={validity}
                    controlledValidity={validity !== undefined || onValidity !== undefined}
                    hideSelectAll={hideSelectAll}
                    inputProps={{ nativeInputProps: { autocomplete: autoComplete } }}
                    onClick={handleDropdownClick}
                    onValueChange={handleValueChange}
                    onValidityChange={handleValidityChange}
                    onVisibleChange={handleVisibleChange}
                    clearButtonProps={clearButtonProps}
                >
                    {prefixProps ? <div slot="prefix">{prefixProps}</div> : undefined}
                    <OwcListStyled size={size} data-virtualization={shouldVirtualize} ref={scrollContainerRef}>
                        <VirtualPadding show={shouldVirtualize} height={virtualPaddingTop} />
                        {renderedOptions}
                        <VirtualPadding show={shouldVirtualize} height={virtualPaddingBottom} />
                        {extraAction ? (
                            <PocListItem
                                className="extraAction"
                                onClick={onClickExtraAction}
                                data-testid={`select-extra-action-${name}`}
                            >
                                {extraAction}
                            </PocListItem>
                        ) : undefined}
                        {hiddenOptions.map(({ value: hiddenOptionValue, text: hiddenOptionText }) => (
                            <OwcListitemHiddenStyled
                                key={`hidden-${hiddenOptionValue}`}
                                value={hiddenOptionValue}
                                data-testid={`select-hidden-option-${name}`}
                            >
                                {hiddenOptionText}
                            </OwcListitemHiddenStyled>
                        ))}
                    </OwcListStyled>
                </PocSelectDropdown>
                <SelectErrorsLayout
                    slot="assistive-text"
                    className={`${spaceForError ? 'space-for-error' : ''} ${floatingAssistiveText ? 'floating-assistive-text' : ''}`}
                >
                    <AssistiveText
                        validity={validity}
                        validationMessages={validationMessages}
                        customValidationCode={customValidationCode}
                    />
                </SelectErrorsLayout>
            </div>
        </SelectLayout>
    );
};
